import React, { useEffect, useState } from "react";
import { getData, postData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { showSuccessToast, showErrorToast } from "../../utils/utils";
import { ChevronsUpDown } from "lucide-react";
import CurrencyInputComponent from "./UIElement/CurrencyInputcomponent";
import DateTimeInput from "./UIElement/DateTimeInput";

export type PaymentStatus = "Pending" | "Paid" | "Failed";
export type PaymentMode = "None" | "Cash" | "Card" | "Zelle" | "Klarna";

export interface DotPayment {
  _id?: string;
  studentId?: string;
  amount: string | number;
  paymentMode: PaymentMode | "";
  transactionId: string;
  paymentDate: string;
  paymentStatus: PaymentStatus;
}

interface PaymentSectionProps {
  leadId: string;
  onSave?: (payload: { leadId: string } & DotPayment) => void;
}
const selectClass =
  "h-[32px] px-4 py-[3px] border leading-[20px] border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg  mb-1 focus:border-transparent transition-all duration-300  text-[13px]";
const inputClass =
  "w-full h-[32px] px-4 py-2 border placeholder:text-[13px] placeholder:text-gray-500  border-gray-400 mt-[2px] hover:border-gray-600 rounded-[5px] bg-gray-50 text-gray-800 font-normal focus:outline-none focus:ring-[1.5px]  focus:ring-greenBg  mb-1 focus:border-transparent transition-all duration-300  text-[13px]";

const PaymentSection: React.FC<PaymentSectionProps> = ({ leadId, onSave }) => {
  const [payment, setPayment] = useState<DotPayment>({
    amount: "",
    paymentMode: "",
    transactionId: "",
    paymentDate: "",
    paymentStatus: "Pending",
  });
  const [transactions, setTransactions] = useState<DotPayment[]>([]);

  const [tabIndex, setTabIndex] = useState(1);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const handleChange = <K extends keyof DotPayment>(
    key: K,
    value: DotPayment[K],
  ) => {
    setPayment((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!payment.amount || !payment.paymentMode) {
      showErrorToast("Amount and Payment Mode are required"); //  toast instead of alert
      return;
    }
    try {
      const response = await postData(ApiUrl.createDOTPayment, {
        ...payment,
        dotId: leadId,
      });
      showSuccessToast("Payment saved successfully!"); // ✅ success toast

      // Refresh transactions
      const updatedTransactions = await getData(
        ApiUrl.getDOTPayments + `${leadId}`,
      );
      setTransactions(updatedTransactions);

      // Reset form
      setPayment({
        amount: "",
        paymentMode: "",
        transactionId: "",
        paymentDate: "",
        paymentStatus: "Pending",
      });
      setShowPaymentForm(false);
    } catch (e) {
      console.error(e);
      showErrorToast("Failed to save payment"); // error toast
    }
  };

  useEffect(() => {
    const api = async () => {
      const response = await getData(ApiUrl.getDOTPayments + `${leadId}`);
      setTransactions(response);
      console.log("Payments:", response);
    };
    api();
  }, [tabIndex, leadId]);

  const statusStyles: Record<PaymentStatus, string> = {
    Paid: "bg-green-100 text-green-700 border border-green-700",
    Pending: "bg-yellow-100 text-yellow-700 border border-yellow-700",
    Failed: "bg-red-100 text-red-700 border border-red-700",
  };
  return (
    <div className="flex flex-col h-[calc(86.5vh-200px)] 2xl:h-[calc(88vh-200px)] 4xl:h-[calc(92vh-200px)]">
      <div className="p-3 m-3 rounded-[10px] flex-1 border border-[#EEEEEE] bg-white pb-6 overflow-y-auto custom-scroll">
        {/* Header */}

        {/* Form */}

        {!showPaymentForm && (
          <button
            type="button"
            onClick={() => setShowPaymentForm(true)}
            className="flex justify-center items-center gap-2
      bg-white
      text-customOrange
      text-sm font-medium
      px-5 py-1
      rounded-full
      text-[14px]
      shadow-[0_4px_10px_rgba(255,165,0,0.18)]
      transition-all duration-300
      h-[32px]
      hover:bg-customOrange
      hover:text-white
      hover:shadow-[0_6px_14px_rgba(255,165,0,0.18)]
      border-[#FFA100]
      border-[1px]
      border-opacity-10   mb-4 border-b-[1px] border-b-[#EEEEEE]"
          >
            + Add Payment
          </button>
        )}
        {showPaymentForm && (
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 rounded-md gap-3 items-end pb-2 mb-2">
            <div className="w-full flex flex-col gap-1">
              <label className="text-[11px] text-gray-500 block mb-1">
                Amount
              </label>
              <CurrencyInputComponent
                name="amount"
                value={payment.amount}
                onValueChange={(value, _) => {
                  handleChange("amount", value ? Number(value) : "");
                }}
              />
            </div>

            <div className="w-full  flex flex-col gap-1">
              <label className="text-[11px] text-gray-500 block mb-1">
                Payment Mode
              </label>
              <select
                value={payment.paymentMode}
                onChange={(e) =>
                  handleChange("paymentMode", e.target.value as PaymentMode)
                }
                className={selectClass}
              >
                <option value="">Select mode</option>
                <option value="None">None</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Zelle">Zelle</option>
                <option value="Klarna">Klarna</option>
              </select>
            </div>

            <div className="w-full  flex flex-col gap-1">
              <label className="text-[11px] text-gray-500 block mb-1">
                Transaction ID
              </label>
              <input
                type="text"
                placeholder="Reference / Txn ID"
                value={payment.transactionId}
                onChange={(e) => handleChange("transactionId", e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="w-full  flex flex-col gap-1 ">
              <label className="text-[11px] text-gray-500 block mb-1 ">
                Payment Date
              </label>
              <DateTimeInput
                height={"31px"}
                name="demoSession.schedule"
                className={
                  "w-full  border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-greenBg focus:border-greenBg"
                }
                value={payment.paymentDate}
                onDateChange={(closeDateInput) => {
                  setPayment((prev): any => ({
                    ...prev,
                    paymentDate: closeDateInput?.toDate(),
                  }));
                }}
                onConfirmed={(acceptedDate) => {
                  setPayment((prev): any => ({
                    ...prev,
                    paymentDate: acceptedDate?.toDate(),
                  }));
                }}
              ></DateTimeInput>
            </div>

            <div className="w-full  flex  flex-col gap-1 ">
              <label className="text-[11px] text-gray-500 block mb-1">
                Status
              </label>
              <select
                value={payment.paymentStatus}
                onChange={(e) =>
                  handleChange("paymentStatus", e.target.value as PaymentStatus)
                }
                className={`${selectClass} `}
              >
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Failed">Failed</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end mt-2 ">
              <div
                className="
      p-[3.5px] 
      rounded-full 
      bg-gradient-to-r 
       shadow-[0.2px_4px_2px_#D2D2D2]
      hover:bg-gradient-to-r from-[#CD84F1] via-[#c1bbf5] to-[#7158E2] 
  hover:bg-[length:200%_200%] hover:animate-[gradient-x_4s_ease_infinite]
    hover:text-white
  "
              >
                <button
                  onClick={handleSubmit}
                  className="
        px-5 py-1 text-sm
        rounded-full
        bg-white
        text-[#7158E2]
        font-semibold
        transition-all duration-200
       hover:bg-gradient-to-r from-[#CD84F1] via-[#c1bbf5] to-[#7158E2] 
  hover:bg-[length:200%_200%] hover:animate-[gradient-x_4s_ease_infinite]
        hover:text-white
      "
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        <>
          <div className="flex items-center gap-2 border-t-[1px] border-t-[#EEEEEE] mb-1.5 mt-1.5 pt-2">
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none">
              <path
                d="M2.91667 18.9584H17.0833C17.8569 18.9584 18.5987 18.6511 19.1457 18.1041C19.6927 17.5571 20 16.8152 20 16.0417V7.70835C20 6.93481 19.6927 6.19294 19.1457 5.64596C18.5987 5.09898 17.8569 4.79169 17.0833 4.79169H13.9583C13.9031 4.79169 13.8501 4.76974 13.811 4.73067C13.7719 4.6916 13.75 4.63861 13.75 4.58335V3.54169C13.75 2.87865 13.4866 2.24276 13.0178 1.77392C12.5489 1.30508 11.913 1.04169 11.25 1.04169H8.75C8.08696 1.04169 7.45107 1.30508 6.98223 1.77392C6.51339 2.24276 6.25 2.87865 6.25 3.54169V4.58335C6.25 4.63861 6.22805 4.6916 6.18898 4.73067C6.14991 4.76974 6.09692 4.79169 6.04167 4.79169H2.91667C2.14312 4.79169 1.40125 5.09898 0.854272 5.64596C0.307291 6.19294 0 6.93481 0 7.70835V16.0417C0 16.8152 0.307291 17.5571 0.854272 18.1041C1.40125 18.6511 2.14312 18.9584 2.91667 18.9584ZM10.9167 13.1L8.41667 12.1834C8.05692 12.0149 7.75633 11.7419 7.55414 11.4C7.35196 11.058 7.25758 10.6631 7.28333 10.2667C7.27655 9.79458 7.44203 9.33619 7.7488 8.97727C8.05556 8.61835 8.4826 8.38351 8.95 8.31669V7.70835C8.95 7.48734 9.03779 7.27538 9.19407 7.1191C9.35035 6.96282 9.56232 6.87502 9.78333 6.87502C10.0043 6.87502 10.2163 6.96282 10.3726 7.1191C10.5289 7.27538 10.6167 7.48734 10.6167 7.70835V8.26669H11.825C12.046 8.26669 12.258 8.35448 12.4143 8.51076C12.5705 8.66705 12.6583 8.87901 12.6583 9.10002C12.6583 9.32103 12.5705 9.533 12.4143 9.68928C12.258 9.84556 12.046 9.93335 11.825 9.93335H9.53333C9.2 9.93335 9.15 10.1 9.15 10.2667C9.15 10.4 9.2 10.55 9.3 10.65L11.5833 11.5667C11.9431 11.7351 12.2437 12.0082 12.4459 12.3501C12.648 12.692 12.7424 13.087 12.7167 13.4834C12.7167 14.35 12.2 15.05 11.25 15.25V16.0417C11.25 16.2627 11.1622 16.4747 11.0059 16.6309C10.8497 16.7872 10.6377 16.875 10.4167 16.875C10.1957 16.875 9.98368 16.7872 9.8274 16.6309C9.67112 16.4747 9.58333 16.2627 9.58333 16.0417V15.4834H8.175C7.95399 15.4834 7.74202 15.3956 7.58574 15.2393C7.42946 15.083 7.34167 14.871 7.34167 14.65C7.34167 14.429 7.42946 14.217 7.58574 14.0608C7.74202 13.9045 7.95399 13.8167 8.175 13.8167H10.4917C10.825 13.8167 10.875 13.65 10.875 13.4834C10.875 13.35 10.825 13.2 10.9167 13.1Z"
                fill="#007070"
              />
            </svg>
            <label className=" text-[15px] text-gray-500 block ">
              Transactions
            </label>
          </div>
          <div className="border rounded-lg overflow-hidden bg-white">
            <table className="w-full border-collapse">
              <thead className="bg-gray-100">
                <tr className="text-[12px] text-gray-600">
                  <th className="px-3 py-2 text-left">
                    <div className="flex items-center gap-1">Transaction ID
                    <span><ChevronsUpDown size="10px" />
                    </span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left ">
                    <div className="flex items-center gap-1">
                    Date
                    <span><ChevronsUpDown size="10px" />
                    </span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left">
                    <div className="flex items-center gap-1">
                    Mode
                     <span><ChevronsUpDown size="10px" />
                    </span>
                    </div>
                    </th>
                  <th className="px-3 py-2 text-left">
                    <div className="flex items-center gap-1">
                    Amount
                     <span><ChevronsUpDown size="10px" />
                    </span>
                    </div>
                  </th>
                  <th className="px-3 py-2 text-left"><div className="flex items-center gap-1">Status
                    <span><ChevronsUpDown size="10px" />
                    </span>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {transactions.map((payment) => (
                  <tr
                    key={payment._id}
                    className="text-[13px] border-t hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 text-gray-500">
                      {payment.transactionId || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {payment.paymentMode}
                    </td>
                    <td className="px-3 py-2 text-gray-500  font-semibold">
                      $ {payment.amount}
                    </td>

                    <td className="px-3 py-2 ">
                      <span
                        className={` px-2 py-[2px] rounded-full text-xs font-semibold  ${
                          statusStyles[payment.paymentStatus]
                        }`}
                      >
                        <span className="inline-block w-[7px] h-[7px] rounded-full bg-current mr-1 "></span>
                        {payment.paymentStatus}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      </div>
    </div>
  );
};

export default PaymentSection;
